import React, { useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  titleId?: string; // For accessibility
  descriptionId?: string; // For aria-describedby
  fullScreen?: boolean;
  centerTitle?: boolean; // Center the title across the full width
  maxWidthClassName?: string; // Override the content max width (non-fullScreen)
};

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  titleId = "modal-title", // Default ID
  descriptionId,
  fullScreen = false,
  centerTitle = false,
  maxWidthClassName = "max-w-md",
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on escape key press
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Focus the modal content when it opens for accessibility
      modalRef.current?.focus();
      // Prevent background scrolling
      document.body.style.overflow = "hidden";
    } else {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    }

    // Cleanup listener and body style on unmount or when isOpen changes
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "auto";
    };
  }, [isOpen, onClose]);

  // Close modal when clicking the overlay
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 ${fullScreen ? "p-2 md:p-8" : "p-4"}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={descriptionId}
    >
      <div
        ref={modalRef}
        className={`relative w-full overflow-y-auto rounded-lg bg-white p-6 shadow-xl outline-none ${fullScreen ? "h-full max-w-none" : `max-h-[calc(100vh-2rem)] ${maxWidthClassName}`}`}
        tabIndex={-1} // Make the modal content focusable
      >
        {title && (
          <h2
            id={titleId}
            className={`mb-4 text-xl font-semibold text-soil ${centerTitle ? "px-8 text-center" : "pr-8"}`}
          >
            {title}
          </h2>
        )}
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-30 rounded-full bg-white/80 p-1 text-gray-400 backdrop-blur-sm transition-colors hover:bg-gray-200 hover:text-gray-700"
          aria-label="Close modal"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
        <div>{children}</div>
      </div>
    </div>
  );
};

export default Modal;
